import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { APP_PATHS } from '@core/constants';
import { locationAddressLine } from '@core/helpers/address.helper';
import { Installation, InstallationStatus } from '@core/models/installation.model';
import { LocationModel } from '@core/models/location.model';
import { InstallationService } from '@core/services/installation.service';
import { FilledButtonComponent } from '@custom-components/general/button';
import { CardComponent } from '@custom-components/general/card/card.component';
import {
  StatusBadgeType,
  StatusChipComponent,
} from '@custom-components/general/status-chip/status-chip.component';
import { TranslateModule } from '@ngx-translate/core';
import {
  InstallationCreateDialogComponent,
  InstallationCreateDialogData,
  installationCreateDialogConfig,
} from '../../../installations/components/installation-create-dialog/installation-create-dialog.component';

export const LOCATION_INSTALLATIONS_PREVIEW = 5;

// The list "View all" opens shows every status as well, so the card and the list agree.
const ALL_STATUSES = 'all';

@Component({
  selector: 'xemex-location-installations-card',
  imports: [CardComponent, FilledButtonComponent, RouterLink, StatusChipComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './location-installations-card.component.html',
})
export class LocationInstallationsCardComponent {
  location = input.required<LocationModel>();
  canCreate = input(false);
  // Changes when the installations at the location may have changed, so they are read again.
  refresh = input(0);

  private readonly installationService = inject(InstallationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  protected readonly APP_PATHS = APP_PATHS;

  private readonly read = rxResource({
    params: () => ({ locationId: this.location().locationId, refresh: this.refresh() }),
    stream: ({ params: { locationId } }) =>
      this.installationService.list({
        locationId,
        status: ALL_STATUSES,
        limit: LOCATION_INSTALLATIONS_PREVIEW,
        offset: 0,
      }),
  });

  // A resource in error throws when its value is read, so the value is only read once it has one.
  protected readonly installations = computed<Installation[]>(() =>
    this.read.hasValue() ? this.read.value().installations : [],
  );
  protected readonly total = computed(() =>
    this.read.hasValue() ? this.read.value().numberOfItems : 0,
  );
  protected readonly loading = computed(() => !this.read.hasValue() && this.read.isLoading());
  protected readonly failed = computed(() => this.read.status() === 'error');

  protected statusChip(status: InstallationStatus): StatusBadgeType {
    return status === 'Active' ? 'success' : 'inactive';
  }

  protected commissioningChip(complete: boolean | null): StatusBadgeType {
    return complete ? 'success' : 'warning';
  }

  viewAll(): void {
    void this.router.navigate(['/', APP_PATHS.Installations], {
      queryParams: { location: this.location().locationId },
    });
  }

  create(): void {
    const location = this.location();
    this.dialog.open<InstallationCreateDialogComponent, InstallationCreateDialogData>(
      InstallationCreateDialogComponent,
      installationCreateDialogConfig({
        location: {
          locationId: location.locationId,
          label: locationAddressLine(location),
          partnerOid: location.partnerOid ?? null,
        },
      }),
    );
  }
}
